import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  ADMIN_SESSION_EXPIRED_EVENT,
  enforceAdminSession,
  expireAdminSession,
  getAdminSessionRemainingMs,
  isAdminAuthenticated
} from "../libraries/auth";
import { canAccessNavRoute, getDefaultAccessibleRoute } from "../libraries/privileges";

function ProtectedRoute({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(() => isAdminAuthenticated());

  useEffect(() => {
    const endSession = () => {
      expireAdminSession();
      setIsAuthenticated(false);
      navigate("/", { replace: true, state: { from: location.pathname, sessionExpired: true } });
    };

    const checkSession = () => {
      if (!enforceAdminSession()) {
        setIsAuthenticated(false);
        navigate("/", { replace: true, state: { from: location.pathname, sessionExpired: true } });
        return false;
      }

      setIsAuthenticated(true);
      return true;
    };

    if (!checkSession()) {
      return undefined;
    }

    const remainingMs = getAdminSessionRemainingMs();
    const expiryTimer = window.setTimeout(endSession, remainingMs + 50);
    const pollTimer = window.setInterval(checkSession, 15_000);

    const onSessionExpired = () => {
      setIsAuthenticated(false);
      navigate("/", { replace: true, state: { from: location.pathname, sessionExpired: true } });
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkSession();
      }
    };

    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, onSessionExpired);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.clearTimeout(expiryTimer);
      window.clearInterval(pollTimer);
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, onSessionExpired);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [location.pathname, navigate]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  if (!canAccessNavRoute(location.pathname)) {
    const fallbackRoute = getDefaultAccessibleRoute();

    if (location.pathname !== fallbackRoute) {
      return <Navigate to={fallbackRoute} replace state={{ from: location.pathname }} />;
    }
  }

  return children;
}

export default ProtectedRoute;
